import { useCallback, useEffect, useMemo, useRef } from 'react';

import { getPersistedValue } from 'shared/utils/persisted-values';
import { DPAY_SESSION_TOKEN_KEY } from 'src/payments/constants';
import { tracker } from 'src/utils/analytics';
import type { AccountSignupAction, AccountSignupMethod, AccountSignupSurface } from 'src/utils/analytics/events';
import { getLogger } from 'src/utils/datadog-logger';

type TrackFailedArgs = {
  errorCode?: string;
  errorMessage?: string;
};

export type UseAccountSignupAnalyticsOptions = {
  surface: AccountSignupSurface | null;
  method: AccountSignupMethod | null;
  dutchiePaySessionToken?: string;
};

export type UseAccountSignupAnalyticsReturn = {
  trackViewed: () => void;
  trackSubmitted: () => void;
  trackSucceeded: () => void;
  trackFailed: (args?: TrackFailedArgs) => void;
};

export const useAccountSignupAnalytics = ({
  surface,
  method,
  dutchiePaySessionToken,
}: UseAccountSignupAnalyticsOptions): UseAccountSignupAnalyticsReturn => {
  const logger = useMemo(() => getLogger('account-signup-analytics'), []);

  const track = useCallback(
    (action: AccountSignupAction, errorFields: TrackFailedArgs = {}): void => {
      if (surface === null || method === null) {
        return;
      }

      try {
        // Resolved at emit time so a token persisted after mount is picked up without subscribing
        // to storage; an explicit option always wins over the persisted fallback.
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' means "absent" and must fall through to the next source; ?? would keep the empty string
        const sessionToken = dutchiePaySessionToken || getPersistedValue(DPAY_SESSION_TOKEN_KEY, '') || undefined;

        tracker.accountSignupAction({
          action,
          method,
          surface,
          ...(sessionToken !== undefined && { dutchiePaySessionToken: sessionToken }),
          ...errorFields,
        });
      } catch (error) {
        logger.warn('Account signup analytics tracking failed', {
          action,
          message: error instanceof Error ? error.message : 'unknown',
        });
      }
    },
    [dutchiePaySessionToken, logger, method, surface]
  );

  const trackViewed = useCallback((): void => track('viewed'), [track]);
  const trackSubmitted = useCallback((): void => track('submitted'), [track]);
  const trackSucceeded = useCallback((): void => track('succeeded'), [track]);
  const trackFailed = useCallback(
    ({ errorCode, errorMessage }: TrackFailedArgs = {}): void => track('failed', { errorCode, errorMessage }),
    [track]
  );

  return { trackViewed, trackSubmitted, trackSucceeded, trackFailed };
};

export type UseAccountSignupViewedOptions = UseAccountSignupAnalyticsOptions & {
  isVisible?: boolean;
};

// Tracks at most one 'viewed' per invocation, emitted only while the signup experience is
// visible; a hidden mount does not burn the once-guard.
export const useAccountSignupViewed = ({ surface, method, isVisible = true }: UseAccountSignupViewedOptions): void => {
  const { trackViewed } = useAccountSignupAnalytics({ surface, method });
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!isVisible || hasTrackedRef.current) {
      return;
    }
    hasTrackedRef.current = true;
    trackViewed();
  }, [isVisible, trackViewed]);
};
